/**
 * SOLID Review
 * S — Strong. One use case: the game screen's ad config — asked for once per free player, and kept.
 * O — Strong. Whether to ask, and what to keep, are the Domain's rules.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and ads-server ports and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { ICommandAdConfigLoaded } from '../../domain/ports/schemas/AdsMoves';
import type { IPortClientAdsServer } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE, type IServiceSerialStep } from '../shared/ClientSteps';

export interface IConfigDepAds {
  readonly decisions: IPortClientDecisions;
  readonly ads: IPortClientAdsServer;
  readonly step: IServiceSerialStep<IPropsClientInput>;
}

/**
 * THE AD SLOT (frontend strict-ports plan, FE4d — AppRoot's `useAdProviderConfig`, moved): plan (whether to ask, marked
 * asked), the server's A/B answer outside the serial section, then keep it. A plan that asks nothing is done; a failed
 * request keeps the defaults, as the hook's `.catch(() => {})` did, and is reported. Not a `ServerRoundTrip`: the plan can
 * decide there is nothing to send.
 */
export class LoadAdConfigUseCase implements IUseCase<ICommandNothing, TPortOutcome> {
  constructor(private readonly deps: IConfigDepAds) {}

  async execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    const { decisions, step, ads } = this.deps;
    const plan = await step.run(ctx, (input) => decisions.planAdConfig(ctx, input, command));
    if (!plan.success) return plan;
    const { request } = plan.value;
    if (request === null) return DONE;
    const answer = await ads.adConfig(ctx, request);
    if (!answer.success) return answer;
    const loaded: ICommandAdConfigLoaded = { playerId: request.playerId, config: answer.value.config };
    return step.outcome(await step.run(ctx, (input) => decisions.adConfigLoaded(ctx, input, loaded)));
  }
}
